import { useState } from 'react';
import { ChevronDown, ChevronRight, Building2, Package, User, Building, Flag } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FirmCombobox } from '@/components/common/FirmCombobox';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/useAuthStore';
import type { SessionPriority } from '@/types/api.types';

const COMMON_CONSULTANTS = [
  'Callan',
  'Mercer',
  'Aon',
  'Cambridge Associates',
  'NEPC',
  'Meketa',
  'Wilshire',
  'RVK',
];

const PRIORITY_OPTIONS: { value: SessionPriority; label: string; color: string }[] = [
  { value: 'high', label: 'High', color: 'border-red-300 bg-red-50 text-red-700' },
  { value: 'normal', label: 'Normal', color: 'border-gray-300 bg-gray-50 text-gray-700' },
  { value: 'low', label: 'Low', color: 'border-blue-300 bg-blue-50 text-blue-700' },
];

export interface UploadDetailsAccordionProps {
  sourceConsultant: string;
  accountName: string;
  priority: SessionPriority;
  firmId: number | null;
  productId: number | null;
  onSourceConsultantChange: (value: string) => void;
  onAccountNameChange: (value: string) => void;
  onPriorityChange: (value: SessionPriority) => void;
  onFirmChange: (firmId: number | null) => void;
  onProductChange: (productId: number | null) => void;
  disabled?: boolean;
  className?: string;
}

export function UploadDetailsAccordion({
  sourceConsultant,
  accountName,
  priority,
  firmId,
  productId,
  onSourceConsultantChange,
  onAccountNameChange,
  onPriorityChange,
  onFirmChange,
  onProductChange,
  disabled = false,
  className,
}: UploadDetailsAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuthStore();
  const isSuperAdmin = user?.isSuperAdmin === true;

  const hasValues = sourceConsultant || accountName || priority !== 'normal' || firmId !== null || productId !== null;

  const handleProductChange = (value: string) => {
    const parsed = parseInt(value, 10);
    onProductChange(isNaN(parsed) ? null : parsed);
  };

  return (
    <Card className={className}>
      <CardHeader
        className={cn(
          'py-3 cursor-pointer select-none',
          !disabled && 'hover:bg-muted/50 transition-colors'
        )}
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Add details (optional)
            {!isExpanded && hasValues && (
              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                Custom
              </span>
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          {/* Consultant + Account */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground flex items-center gap-1.5" htmlFor="source-consultant">
                <User className="h-3.5 w-3.5" />
                Source Consultant
              </label>
              <Input
                id="source-consultant"
                placeholder="e.g. Callan, Mercer..."
                value={sourceConsultant}
                onChange={(e) => onSourceConsultantChange(e.target.value)}
                disabled={disabled}
                maxLength={200}
                list="consultant-suggestions"
              />
              <datalist id="consultant-suggestions">
                {COMMON_CONSULTANTS.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground flex items-center gap-1.5" htmlFor="account-name">
                <Building className="h-3.5 w-3.5" />
                Account Name
              </label>
              <Input
                id="account-name"
                placeholder="Enter account name..."
                value={accountName}
                onChange={(e) => onAccountNameChange(e.target.value)}
                disabled={disabled}
                maxLength={255}
              />
            </div>
          </div>

          {/* Priority */}
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Flag className="h-3.5 w-3.5" />
              Priority
            </label>
            <div className="flex gap-2">
              {PRIORITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onPriorityChange(option.value)}
                  disabled={disabled}
                  className={cn(
                    'flex-1 py-1.5 px-3 text-sm font-medium rounded-md border transition-colors',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    priority === option.value
                      ? option.color
                      : 'border-transparent bg-muted/50 text-muted-foreground hover:bg-muted'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Firm/Product Override (SuperAdmin only) */}
          {isSuperAdmin && (
            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div className="space-y-1.5">
                <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Firm
                </label>
                <div className={cn(disabled && 'pointer-events-none opacity-50')}>
                  <FirmCombobox value={firmId} onChange={onFirmChange} />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5" />
                  Product ID
                </label>
                <Input
                  type="number"
                  placeholder="Optional"
                  value={productId ?? ''}
                  onChange={(e) => handleProductChange(e.target.value)}
                  disabled={disabled}
                  min={1}
                />
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
